import { useEffect, useState } from 'react';
import { useOp } from '../../api.ts';
import { useRouter } from '../../router.tsx';
import { step, useKeys } from '../../ui/keys.ts';
import { Drawer, Empty, ErrorBox, Loading } from '../../ui/kit.tsx';
import type { AuditEntry } from './types.ts';

const FILTER_KEYS = ['actor', 'operation', 'entity', 'outcome', 'source', 'from', 'to'] as const;

const OUTCOME_TONE: Record<string, string> = { ok: 'badge-ok', failed: 'badge-warn', error: 'badge-warn', denied: 'badge-warn', rejected: 'badge-warn' };

/** Who changed what, from Studio, workers and the companion. Read only. */
export function Audit() {
  const { query, setQuery } = useRouter();
  const filters = Object.fromEntries(FILTER_KEYS.map((k) => [k, query.get(k) ?? ''])) as Record<(typeof FILTER_KEYS)[number], string>;
  const input = Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== ''));
  const log = useOp<{ entries: AuditEntry[]; scope: string; companionIncluded: boolean }>('admin.audit.list', { ...input, limit: 200 });
  const [cursor, setCursor] = useState(0);
  const [open, setOpen] = useState<AuditEntry | null>(null);
  const entries = log.data?.entries ?? [];
  useEffect(() => setCursor(0), [JSON.stringify(input)]);
  useKeys(
    {
      j: () => setCursor((c) => step(c, 1, entries.length)),
      k: () => setCursor((c) => step(c, -1, entries.length)),
      enter: () => entries[cursor] && setOpen(entries[cursor]!),
      o: () => entries[cursor] && setOpen(entries[cursor]!),
    },
    open === null,
  );

  const field = (key: (typeof FILTER_KEYS)[number], label: string, props: Record<string, unknown> = {}) => (
    <label className="audit-filter">
      {label}
      <input defaultValue={filters[key]} onBlur={(e) => setQuery({ [key]: e.target.value || null }, { replace: true })} onKeyDown={(e) => e.key === 'Enter' && setQuery({ [key]: e.currentTarget.value || null }, { replace: true })} {...props} />
    </label>
  );

  return (
    <section className="page">
      <header className="page-head">
        <div>
          <h1>Audit log</h1>
          <p className="muted">
            {log.data ? (log.data.scope === 'everything' ? 'All organizations.' : 'Changes by people in the organizations you administer.') : ' '} Newest first, up to 200 entries.
          </p>
        </div>
      </header>
      <div className="toolbar audit-filters">
        {field('actor', 'Who', { placeholder: 'email' })}
        {field('operation', 'Operation', { placeholder: 'e.g. sites.update' })}
        {field('entity', 'Entity', { placeholder: 'ref or text, e.g. prp_…' })}
        <label className="audit-filter">
          Outcome
          <select value={filters.outcome} onChange={(e) => setQuery({ outcome: e.target.value || null }, { replace: true })}>
            <option value="">Any</option>
            <option value="ok">OK</option>
            <option value="failed">Failed</option>
            <option value="denied">Denied</option>
            <option value="error">Error (companion)</option>
          </select>
        </label>
        <label className="audit-filter">
          Source
          <select value={filters.source} onChange={(e) => setQuery({ source: e.target.value || null }, { replace: true })}>
            <option value="">Studio + companion</option>
            <option value="studio">Studio and workers</option>
            <option value="companion">Companion</option>
          </select>
        </label>
        {field('from', 'From', { type: 'date' })}
        {field('to', 'To', { type: 'date' })}
      </div>
      {log.error ? <ErrorBox error={log.error} onRetry={log.reload} /> : null}
      {!log.data && log.loading ? <Loading /> : null}
      {log.data && entries.length === 0 ? <Empty>No entries match.</Empty> : null}
      {entries.length ? (
        <table className="table compact" aria-label="Audit entries">
          <thead>
            <tr>
              <th scope="col">When</th>
              <th scope="col">Who</th>
              <th scope="col">Operation</th>
              <th scope="col">Outcome</th>
              <th scope="col">Via</th>
              <th scope="col">Summary</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((entry, index) => (
              <tr key={entry.key} className={`clickable ${index === cursor ? 'cursor' : ''}`} onClick={() => setOpen(entry)} onMouseEnter={() => setCursor(index)}>
                <td className="small nowrap">{new Date(entry.occurredAt).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'medium' })}</td>
                <td className="small">{entry.actorId}</td>
                <td>
                  <code>{entry.operation}</code>
                </td>
                <td>
                  <span className={`badge ${OUTCOME_TONE[entry.outcome] ?? ''}`}>{entry.outcome}</span>
                </td>
                <td className="small">{entry.source === 'companion' ? `companion · ${entry.via}` : entry.via}</td>
                <td className="small truncate">{entry.error ?? summary(entry.input)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
      {open ? <EntryDrawer entry={open} onClose={() => setOpen(null)} /> : null}
    </section>
  );
}

function summary(input: unknown): string {
  if (!input || typeof input !== 'object') return '';
  return Object.entries(input as Record<string, unknown>)
    .slice(0, 4)
    .map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : String(v)}`)
    .join(' · ')
    .slice(0, 140);
}

/** Flattens nested objects to `a.b.c` paths for a side-by-side table. */
function flatten(value: unknown, prefix = ''): [string, string][] {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return prefix ? [[prefix, JSON.stringify(value)]] : [];
  return Object.entries(value as Record<string, unknown>).flatMap(([k, v]) => flatten(v, prefix ? `${prefix}.${k}` : k));
}

function EntryDrawer({ entry, onClose }: { entry: AuditEntry; onClose: () => void }) {
  const input = new Map(flatten(entry.input));
  const result = new Map(flatten(entry.result));
  const paths = [...new Set([...input.keys(), ...result.keys()])];
  return (
    <Drawer title={entry.operation} onClose={onClose} wide>
      <p className="muted small">
        {new Date(entry.occurredAt).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'medium' })} · {entry.actorId} ({entry.actorKind}) · {entry.source} via {entry.via} ·{' '}
        <span className={`badge ${OUTCOME_TONE[entry.outcome] ?? ''}`}>{entry.outcome}</span>
      </p>
      {entry.error ? <p className="error-box">{entry.error}</p> : null}
      <p className="muted small">What was asked (input) next to what the operation answered (result). Secrets are never logged.</p>
      {paths.length ? (
        <table className="table compact diff">
          <thead>
            <tr>
              <th scope="col">Field</th>
              <th scope="col">Input</th>
              <th scope="col">Result</th>
            </tr>
          </thead>
          <tbody>
            {paths.map((path) => (
              <tr key={path} className={input.has(path) && result.has(path) && input.get(path) !== result.get(path) ? 'row-warn' : undefined}>
                <th scope="row">
                  <code>{path}</code>
                </th>
                <td className="mono small">{input.get(path) ?? ''}</td>
                <td className="mono small">{result.get(path) ?? ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <Empty>No input or result recorded.</Empty>
      )}
    </Drawer>
  );
}
